import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type {
  GetRiskMetricGroupCommandOutput,
  RiskMetric,
  RiskScoreWindow,
} from '@amzn/gbsa-helios-typescript-client';
import { Button, ButtonVariant } from '@amzn/stencil-react-components/button';
import { Col } from '@amzn/stencil-react-components/layout';
import {
  MessageBanner,
  MessageBannerType,
} from '@amzn/stencil-react-components/message-banner';
import { H2, Text } from '@amzn/stencil-react-components/text';

import { useCurrentUser } from 'src/v2/shared/auth';
import { useMessages, type Messages } from 'src/v2/shared/i18n/useMessages';
import { Divider } from 'src/v2/shared/ui/Divider';
import { useIsMobile } from 'src/v2/shared/ui/useIsMobile';
import { useGetRiskMetricGroupQuery } from '../api';
import { useRiskMetricGroupExport } from '../hooks';
import {
  buildMetricTree,
  buildRiskMetricGroupRequest,
  type RiskMetricNode,
} from '../logic/riskMetricGroup';
import { decodeRiskScoreWindow } from '../logic/riskTimeWindow';
import { RISK_ENGINE_BUNDLE, type RiskEngineMessageKey } from '../translations';
import { RISK_ENGINE_TEST_IDS } from '../testIds';
import { BreakdownHeader, type BreakdownExportControl } from './BreakdownHeader';
import { RiskEnginePageSkeleton } from './RiskEnginePageSkeleton';
import { RiskKpiSummary } from './RiskKpiSummary';
import { RISK_TREE_COLUMN_COUNT, RiskMetricTreeTable } from './RiskMetricTreeTable';

type RiskEngineMessages = Messages<RiskEngineMessageKey>;

/** Stable empty reference so a pending/disabled query doesn't churn the tree memo. */
const EMPTY_METRICS: readonly RiskMetric[] = [];

/** Stable empty driver list for the table's loading skeleton. */
const EMPTY_ROOTS: readonly RiskMetricNode[] = [];

/** The URL param that carries the selected scoring window, deep-linkable and refetch-keyed. */
const TIME_WINDOW_PARAM = 'timeWindow';

/** Whether the group loaded with drivers to show — gates both the table render and the export. */
function hasBreakdownRows(
  isError: boolean,
  isLoading: boolean,
  data: GetRiskMetricGroupCommandOutput | undefined,
  tree: RiskMetricNode | undefined,
): boolean {
  if (isError || isLoading || !data || data.hasData === false) return false;
  return !!tree && tree.children.length > 0;
}

interface ExportBannerProps {
  readonly show: boolean;
  readonly type: MessageBannerType;
  readonly dataTestId: string;
  readonly message: string;
}

/** A dismiss-free status banner for the export outcome (failed, or succeeded with no rows). */
function ExportBanner({
  show,
  type,
  dataTestId,
  message,
}: ExportBannerProps): JSX.Element | null {
  if (!show) return null;
  return (
    <MessageBanner type={type} isDismissible={false} dataTestId={dataTestId}>
      {message}
    </MessageBanner>
  );
}

interface BreakdownBodyProps {
  readonly isError: boolean;
  readonly isLoading: boolean;
  readonly data: GetRiskMetricGroupCommandOutput | undefined;
  readonly tree: RiskMetricNode | undefined;
  readonly t: RiskEngineMessages['t'];
  readonly onRetry: () => void;
}

/**
 * The drill-down body: the error banner, the driver table's skeleton while loading, the empty
 * state, or the tree table.
 */
function BreakdownBody({
  isError,
  isLoading,
  data,
  tree,
  t,
  onRetry,
}: BreakdownBodyProps): JSX.Element {
  if (isError) {
    return (
      <MessageBanner
        dataTestId={RISK_ENGINE_TEST_IDS.breakdownError}
        isDismissible={false}
        type={MessageBannerType.Error}
      >
        {t('drilldownError')}
        <Button variant={ButtonVariant.Tertiary} onClick={onRetry}>
          {t('drilldownRetry')}
        </Button>
      </MessageBanner>
    );
  }
  if (isLoading || !data) {
    return (
      <Col aria-busy dataTestId={RISK_ENGINE_TEST_IDS.breakdownLoading}>
        <RiskMetricTreeTable
          roots={EMPTY_ROOTS}
          ariaLabel={t('drilldownLoading')}
          loading
        />
      </Col>
    );
  }
  if (!hasBreakdownRows(isError, isLoading, data, tree) || !tree) {
    return (
      <Col dataTestId={RISK_ENGINE_TEST_IDS.breakdownEmpty}>
        <Text color="color.text.secondary">{t('drilldownEmpty')}</Text>
      </Col>
    );
  }
  return <RiskMetricTreeTable roots={tree.children} ariaLabel={t('drilldownCaption')} />;
}

interface DriversIntroProps {
  readonly root: RiskMetric;
  readonly metricGroupId: string;
  /** Whether the driver table will render; the heading + caption only introduce a real table. */
  readonly hasDrivers: boolean;
  readonly t: RiskEngineMessages['t'];
  readonly f: RiskEngineMessages['f'];
}

/**
 * The root KPI summary, then — only when the driver table renders — a divider and the heading +
 * caption that introduce it. A root with no drivers keeps its summary but gets no heading above
 * the "no drivers" empty state.
 */
function DriversIntro({
  root,
  metricGroupId,
  hasDrivers,
  t,
  f,
}: DriversIntroProps): JSX.Element {
  return (
    <>
      <RiskKpiSummary metric={root} />
      {hasDrivers ? (
        <>
          <Divider />
          <Col gridGap="dimensions.spacing.50">
            <H2 fontSize="T300">
              {f('breakdownDriversTitle', { group: metricGroupId })}
            </H2>
            <Text fontSize="T100" color="color.text.secondary">
              {t('breakdownDriversCaption')}
            </Text>
          </Col>
        </>
      ) : null}
    </>
  );
}

/**
 * Per-station metric-group impact breakdown — the drill-down. Reads the station (`location`), metric
 * group (`metricGroupId`) and scoring window (`timeWindow`) from the URL, fetches the whole group in
 * one call, rebuilds the hierarchy, and renders the root KPI header (with the window toggle) above
 * its nested driver tree (up to four levels). The window is held in the URL so it is deep-linkable
 * and survives refresh; changing it rewrites the param, which re-keys the query and refetches.
 */
export function RiskMetricBreakdownPage(): JSX.Element {
  const { businessUnit } = useCurrentUser();
  const {
    t,
    f,
    isLoading: isBundleLoading,
  } = useMessages<RiskEngineMessageKey>(RISK_ENGINE_BUNDLE);
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = searchParams.get('location') ?? '';
  const metricGroupId = searchParams.get('metricGroupId') ?? '';
  const timeWindow = decodeRiskScoreWindow(searchParams.get(TIME_WINDOW_PARAM));

  const request = useMemo(
    () =>
      buildRiskMetricGroupRequest({
        businessUnitId: businessUnit,
        location,
        metricGroupId,
        timeWindow,
      }),
    [businessUnit, location, metricGroupId, timeWindow],
  );

  const ready = !!businessUnit && !!location && !!metricGroupId;
  const { data, isLoading, isError, refetch } = useGetRiskMetricGroupQuery(request, {
    enabled: ready,
  });

  const tree = useMemo(() => buildMetricTree(data?.metrics ?? EMPTY_METRICS), [data]);

  const handleWindowChange = useCallback(
    (next: RiskScoreWindow): void => {
      setSearchParams(
        (prev) => {
          const nextParams = new URLSearchParams(prev);
          nextParams.set(TIME_WINDOW_PARAM, next);
          return nextParams;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Server-generated CSV of this station's whole metric-group tree (root KPI + all sub-drivers) for
  // the selected window — the backend reads the same scope as GetRiskMetricGroup, so the file
  // matches what the page shows regardless of which rows are expanded.
  const {
    triggerExport,
    isExporting,
    showError: showExportError,
    showEmpty: showExportEmpty,
  } = useRiskMetricGroupExport({
    businessUnitId: businessUnit,
    location,
    metricGroupId,
    timeWindow,
  });

  const handleRetry = useCallback(() => void refetch(), [refetch]);

  // Only offer the export once the tree has loaded with drivers — mirrors the states where the
  // table renders.
  const hasExportableData = hasBreakdownRows(isError, isLoading, data, tree);
  const exportControl = useMemo<BreakdownExportControl>(
    () => ({ disabled: !hasExportableData, isExporting, onExport: triggerExport }),
    [hasExportableData, isExporting, triggerExport],
  );

  // Until the translations load, every label would be a raw message key — show the page skeleton
  // instead. The group request above is already in flight, so nothing waits on this.
  if (isBundleLoading) {
    return (
      <RiskEnginePageSkeleton
        columnCount={RISK_TREE_COLUMN_COUNT}
        dataTestId={RISK_ENGINE_TEST_IDS.breakdownLoading}
      />
    );
  }

  // No station/group in the URL — nothing to fetch or filter, so show the neutral empty state.
  if (!ready) {
    return (
      <Col
        dataTestId={RISK_ENGINE_TEST_IDS.breakdownEmpty}
        padding="dimensions.spacing.500"
      >
        <Text color="color.text.secondary">{t('drilldownEmpty')}</Text>
      </Col>
    );
  }

  const root = tree?.metric;

  return (
    <Col
      dataTestId={RISK_ENGINE_TEST_IDS.breakdown}
      gridGap="dimensions.spacing.300"
      padding={{
        top: 'dimensions.spacing.300',
        bottom: 'dimensions.spacing.600',
        left: 'dimensions.spacing.200',
        right: 'dimensions.spacing.200',
      }}
      width="100%"
    >
      <BreakdownHeader
        station={location}
        metricGroupId={metricGroupId}
        metricName={root ? (root.displayName ?? root.metric) : undefined}
        businessUnit={businessUnit}
        scoringDate={data?.scoringDate}
        timeWindow={timeWindow}
        onWindowChange={handleWindowChange}
        {...(isMobile ? {} : { exportControl })}
      />

      <ExportBanner
        show={!isMobile && showExportError}
        type={MessageBannerType.Error}
        dataTestId={RISK_ENGINE_TEST_IDS.breakdownExportError}
        message={t('exportError')}
      />
      <ExportBanner
        show={!isMobile && showExportEmpty}
        type={MessageBannerType.Warning}
        dataTestId={RISK_ENGINE_TEST_IDS.breakdownExportEmpty}
        message={t('breakdownExportEmpty')}
      />

      <Col gridGap="dimensions.spacing.300" minWidth={0} width="100%">
        {root ? (
          <DriversIntro
            root={root}
            metricGroupId={metricGroupId}
            hasDrivers={hasExportableData}
            t={t}
            f={f}
          />
        ) : null}
        <BreakdownBody
          isError={isError}
          isLoading={isLoading}
          data={data}
          tree={tree}
          t={t}
          onRetry={handleRetry}
        />
      </Col>
    </Col>
  );
}
